import { useState } from 'react';
import {
  Type, AlignLeft, Info, Table2,
  Video, Code, Plus, Trash2,
  ChevronUp, ChevronDown, Save
} from 'lucide-react';

export interface ContentBlock {
  type: string;
  text?: string;
  variant?: string;
  url?: string;
  headers?: string[];
  rows?: string[][];
}

interface Props {
  initialBlocks: ContentBlock[];
  onSave: (blocks: ContentBlock[]) => Promise<void>;
  saving?: boolean;
}

const BLOCK_TYPES = [
  { type: 'heading',   icon: Type,      label: 'Heading',    color: 'text-white' },
  { type: 'paragraph', icon: AlignLeft, label: 'Paragraph',  color: 'text-gray-300' },
  { type: 'callout',   icon: Info,      label: 'Callout',    color: 'text-blue-400' },
  { type: 'table',     icon: Table2,    label: 'Table',      color: 'text-green-400' },
  { type: 'video',     icon: Video,     label: 'Video URL',  color: 'text-red-400' },
  { type: 'code',      icon: Code,      label: 'Code',       color: 'text-yellow-400' },
];

export function ContentEditor({ initialBlocks, onSave, saving }: Props) {
  const [blocks, setBlocks] = useState<ContentBlock[]>(
    initialBlocks.length > 0 ? initialBlocks : [
      { type: 'heading',   text: '' },
      { type: 'paragraph', text: '' },
    ]
  );
  const [showAddMenu, setShowAddMenu] = useState<number | null>(null);

  const updateBlock = (idx: number, updates: Partial<ContentBlock>) => {
    setBlocks(prev => prev.map((b, i) =>
      i === idx ? { ...b, ...updates } : b
    ));
  };

  const addBlock = (afterIdx: number, type: string) => {
    const newBlock: ContentBlock = type === 'table'
      ? { type, headers: ['Column 1', 'Column 2'], rows: [['', '']] }
      : type === 'callout'
      ? { type, variant: 'info', text: '' }
      : { type, text: '' };

    setBlocks(prev => [
      ...prev.slice(0, afterIdx + 1),
      newBlock,
      ...prev.slice(afterIdx + 1),
    ]);
    setShowAddMenu(null);
  };

  const removeBlock = (idx: number) => {
    if (blocks.length <= 1) return;
    setBlocks(prev => prev.filter((_, i) => i !== idx));
  };

  const moveBlock = (idx: number, direction: 'up' | 'down') => {
    const newBlocks = [...blocks];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= blocks.length) return;
    [newBlocks[idx], newBlocks[swapIdx]] = [newBlocks[swapIdx], newBlocks[idx]];
    setBlocks(newBlocks);
  };

  const addTableRow = (blockIdx: number) => {
    const block = blocks[blockIdx];
    const cols = block.headers?.length || 2;
    updateBlock(blockIdx, {
      rows: [...(block.rows || []), Array(cols).fill('')]
    });
  };

  const inputClass = `w-full bg-gray-800 border border-gray-700
    rounded-lg px-3 py-2 text-white text-sm
    focus:outline-none focus:border-green-400 resize-none`;

  const renderBlockEditor = (block: ContentBlock, idx: number) => {
    return (
      <div key={idx}
           className="group border border-gray-800 rounded-xl p-4
                      bg-gray-900 hover:border-gray-700 transition-colors">

        {/* Block toolbar */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            {block.type}
            {block.variant && ` · ${block.variant}`}
          </span>
          <div className="flex items-center gap-1 opacity-0
                          group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => moveBlock(idx, 'up')}
              disabled={idx === 0}
              className="p-1 text-gray-500 hover:text-white
                         disabled:opacity-30 transition-colors"
            >
              <ChevronUp size={14} />
            </button>
            <button
              onClick={() => moveBlock(idx, 'down')}
              disabled={idx === blocks.length - 1}
              className="p-1 text-gray-500 hover:text-white
                         disabled:opacity-30 transition-colors"
            >
              <ChevronDown size={14} />
            </button>
            <button
              onClick={() => removeBlock(idx)}
              className="p-1 text-gray-500 hover:text-red-400
                         transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {/* Block content */}
        {block.type === 'heading' && (
          <input
            value={block.text || ''}
            onChange={e => updateBlock(idx, { text: e.target.value })}
            placeholder="Enter heading..."
            className={inputClass}
          />
        )}

        {block.type === 'paragraph' && (
          <textarea
            value={block.text || ''}
            onChange={e => updateBlock(idx, { text: e.target.value })}
            placeholder="Enter paragraph text..."
            rows={4}
            className={inputClass}
          />
        )}

        {block.type === 'callout' && (
          <div className="space-y-2">
            <select
              value={block.variant || 'info'}
              onChange={e => updateBlock(idx, { variant: e.target.value })}
              className={inputClass}
            >
              <option value="info">Info (Blue)</option>
              <option value="example">Example (Green)</option>
              <option value="warning">Warning (Yellow)</option>
              <option value="tip">Tip (Purple)</option>
            </select>
            <textarea
              value={block.text || ''}
              onChange={e => updateBlock(idx, { text: e.target.value })}
              placeholder="Callout text..."
              rows={2}
              className={inputClass}
            />
          </div>
        )}

        {block.type === 'table' && (
          <div className="space-y-2">
            {/* Headers */}
            <div className="text-xs text-gray-500 mb-1">
              Column Headers
            </div>
            <div className="flex gap-2 mb-3">
              {(block.headers || []).map((h, hi) => (
                <input
                  key={hi}
                  value={h}
                  onChange={e => {
                    const headers = [...(block.headers || [])];
                    headers[hi] = e.target.value;
                    updateBlock(idx, { headers });
                  }}
                  placeholder={`Column ${hi + 1}`}
                  className={inputClass + ' flex-1'}
                />
              ))}
              <button
                onClick={() => updateBlock(idx, {
                  headers: [...(block.headers || []), 'New Column'],
                  rows: (block.rows || []).map(r => [...r, ''])
                })}
                className="text-green-400 text-xs px-2 hover:text-green-300"
              >
                + Col
              </button>
            </div>

            {/* Rows */}
            <div className="text-xs text-gray-500 mb-1">Rows</div>
            {(block.rows || []).map((row, ri) => (
              <div key={ri} className="flex gap-2">
                {row.map((cell, ci) => (
                  <input
                    key={ci}
                    value={cell}
                    onChange={e => {
                      const rows = (block.rows || []).map((r, rIdx) =>
                        rIdx === ri
                          ? r.map((c, cIdx) => cIdx === ci ? e.target.value : c)
                          : r
                      );
                      updateBlock(idx, { rows });
                    }}
                    placeholder="Cell value"
                    className={inputClass + ' flex-1'}
                  />
                ))}
                <button
                  onClick={() => updateBlock(idx, {
                    rows: (block.rows || []).filter((_, rIdx) => rIdx !== ri)
                  })}
                  className="text-red-400 hover:text-red-300 px-1"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <button
              onClick={() => addTableRow(idx)}
              className="text-green-400 text-xs hover:text-green-300"
            >
              + Add row
            </button>
          </div>
        )}

        {block.type === 'video' && (
          <input
            value={block.url || ''}
            onChange={e => updateBlock(idx, { url: e.target.value })}
            placeholder="https://example.com/video.mp4"
            className={inputClass}
          />
        )}

        {block.type === 'code' && (
          <textarea
            value={block.text || ''}
            onChange={e => updateBlock(idx, { text: e.target.value })}
            placeholder="Enter code here..."
            rows={6}
            className={inputClass + ' font-mono text-green-400'}
          />
        )}

        {/* Add block after this one */}
        <div className="mt-3 relative">
          <button
            onClick={() => setShowAddMenu(
              showAddMenu === idx ? null : idx
            )}
            className="flex items-center gap-1 text-xs text-gray-600
                       hover:text-green-400 transition-colors"
          >
            <Plus size={12} /> Add block below
          </button>

          {showAddMenu === idx && (
            <div className="absolute top-6 left-0 z-10 bg-gray-800
                            border border-gray-700 rounded-xl p-2
                            flex flex-wrap gap-2 shadow-xl">
              {BLOCK_TYPES.map(bt => (
                <button
                  key={bt.type}
                  onClick={() => addBlock(idx, bt.type)}
                  className={`flex items-center gap-1.5 px-3 py-2
                              bg-gray-700 hover:bg-gray-600
                              rounded-lg text-xs font-semibold
                              transition-colors ${bt.color}`}
                >
                  <bt.icon size={12} />
                  {bt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Block list */}
      <div className="space-y-3 mb-4">
        {blocks.map((block, idx) => renderBlockEditor(block, idx))}
      </div>

      {/* Add first block if empty */}
      {blocks.length === 0 && (
        <div className="text-center py-8">
          <button
            onClick={() => addBlock(-1, 'heading')}
            className="text-green-400 font-semibold hover:text-green-300"
          >
            + Add your first block
          </button>
        </div>
      )}

      {/* Save button */}
      <button
        onClick={() => onSave(blocks)}
        disabled={saving}
        className="w-full bg-green-400 text-gray-950 font-black
                   py-3 rounded-xl hover:bg-green-300 transition-colors
                   disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Save size={16} />
        {saving ? 'Saving...' : 'Save Lesson Content'}
      </button>
    </div>
  );
}